import loadjs from 'loadjs';
import styled from 'styled-components';
import videojs_ from 'video.js';

/** Plugin CSS. */
/* eslint-disable import/no-internal-modules */
import 'video.js/dist/video-js.min.css';
import '@silvermine/videojs-quality-selector/dist/css/quality-selector.css';
import '@silvermine/videojs-chromecast/dist/silvermine-videojs-chromecast.css';
import '@silvermine/videojs-airplay/dist/silvermine-videojs-airplay.css';

/** videojs plugins */
/* eslint-disable import/no-unassigned-import */
import 'videojs-http-source-selector';
import 'videojs-contrib-quality-levels';
import 'videojs-mux';
import { EventTracking, Playlist, TimeDisplayPlugin, VttThumbnails } from './plugins';

// Register vttThumbnails plugin
videojs_.registerPlugin('VttThumbnails', VttThumbnails);
videojs_.registerPlugin('Playlist', Playlist);
videojs_.registerPlugin('EventTracking', EventTracking);
videojs_.registerComponent('TimeDisplay', TimeDisplayPlugin);

// Initialize chromecast and airplay
/* eslint-disable @typescript-eslint/no-var-requires, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-require-imports, unicorn/prefer-module */
require('@silvermine/videojs-chromecast')(videojs_, { preloadWebComponents: true });
require('@silvermine/videojs-airplay')(videojs_);

/* eslint-enable */

/**
 * Load Google cast framework.
 * Silvermine googlecast does not load this by default.
 * This should be loaded explicitly.
 */
void new Promise<void>((resolve, reject) => {
  window.__onGCastApiAvailable = (isAvailable: boolean): void => {
    if (isAvailable) {
      resolve();
    } else {
      reject(new Error('No chrome cast available'));
    }
  };
  // eslint-disable-next-line
  void loadjs('https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1', () => {
    // eslint-disable-next-line no-console
    console.log('Loaded chrome cast.');
  });
});

const videojs = videojs_;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;

  > video {
    border-radius: 8px;
  }

  &.vjs-cast-live {
    > video {
      opacity: 0;
    }
    .vjs-control-bar {
      opacity: 1 !important;
    }
  }

  /* Control bar styles. */
  .vjs-control-bar {
    bottom: 5px;
    left: 5px;
    right: 5px;
    width: auto;
    padding: 10px 0px;
    height: 40px;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 8px;

    .vjs-control {
      display: flex;
      align-items: center;
      justify-content: center;

      /* FONT AWESOME ICONS HERE. */
      .vjs-icon-placeholder:before {
        /* font-size: 15px; */
      }
      &.vjs-paused,
      &.vjs-playing,
      &.vjs-fullscreen-control {
        .vjs-icon-placeholder:before {
          font-family: 'Font Awesome 5 Pro';
          font-weight: 900;
          font-size: 14px;
        }
      }
      &.vjs-ended.vjs-paused > span:before {
        content: '\f0e2';
      }
      &.vjs-paused > span:before {
        content: '\f04b';
      }
      &.vjs-playing > span:before {
        content: '\f04c';
      }
      &.vjs-fullscreen-control > span:before {
        content: '\f424';
      }

      /* END OF FONT AWESOME ICONS */

      &.vjs-cast-control {
        transform: scale(0.5);
      }
      &.vjs-time-control {
        white-space: nowrap;
      }
      &.vjs-http-source-selector {
        > .vjs-menu-button::before {
          font-size: 18px;
        }
      }
      > span::before {
        position: relative;
      }

      /* HIDE ON MOBILE */
      &.vjs-cast-control,
      &.vjs-http-source-selector,
      &.vjs-picture-in-picture-control,
      &.vjs-volume-panel {
        display: none;
      }
    }
  }

  &.vjs-live {
    .vjs-time-control-duration {
      display: none;
    }
    .vjs-seek-to-live-control {
      display: flex !important;
    }
  }

  @media (orientation: landscape) {
    .vjs-control-bar {
      bottom: 20px;
      left: 20px;
      right: 20px;
      width: auto;
      padding: 10px;
      height: 55px;

      .vjs-control {
        /* SHOW ON MOBILE */
        &.vjs-cast-control,
        &.vjs-http-source-selector,
        &.vjs-picture-in-picture-control,
        &.vjs-volume-panel {
          display: flex;
        }
        &.vjs-chromecast-button {
          > .vjs-icon-placeholder {
            width: 17px;
            height: 17px;
          }
        }
        &.vjs-airplay-button {
          > .vjs-icon-placeholder {
            width: 18px;
            height: 18px;
          }
        }
      }
    }
  }
  .x-viewport-md & {
    .vjs-control-bar {
      bottom: 20px;
      left: 20px;
      right: 20px;
      width: auto;
      padding: 10px;
      height: 55px;

      .vjs-control {
        /* SHOW ON MOBILE */
        &.vjs-cast-control,
        &.vjs-chromecast-button,
        &.vjs-http-source-selector,
        &.vjs-picture-in-picture-control,
        &.vjs-volume-panel {
          display: flex;
        }
      }
    }
  }

  .vjs-seek-to-live-control {
    display: none !important;
    background-color: var(--gamma);
    margin-left: 10px;
    line-height: 1;
    padding: 4px 8px;
    border-radius: 8px;
    height: auto;
    &.vjs-at-live-edge {
      > .vjs-icon-placeholder {
        color: var(--white);
      }
    }
  }

  /* Big Play Button styles. */
  &.video-js {
    > .vjs-big-play-button {
      display: flex;
      width: 90px;
      height: 90px;
      border: 0;
      margin-top: -45px;
      margin-bottom: --45px;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background-color: rgba(0, 0, 0, 0.7);

      > .vjs-icon-placeholder::before {
        position: static;
        font-size: 32px;
        font-family: 'Font Awesome 5 Pro';
        font-weight: 900;
        content: '\f04b';
        margin-left: 6px;
      }
    }
    > .vjs-control-bar {
      display: none;
    }

    &.vjs-can-play {
      > .vjs-big-play-button {
        display: none;
      }
      > .vjs-control-bar {
        display: flex;
      }
    }
  }
`;

export { videojs, StyledVideo };
